import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Box } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';

interface SwipeableNotificationItemProps {
  children: React.ReactNode;
  onDismiss: () => void;
  disabled?: boolean;
  /** Set to a changing value to programmatically trigger the dismiss animation */
  dismissTrigger?: number;
}

const DIRECTION_LOCK_THRESHOLD = 10;
const DISMISS_THRESHOLD = 100;

export const SwipeableNotificationItem: React.FC<SwipeableNotificationItemProps> = ({
  children,
  onDismiss,
  disabled = false,
  dismissTrigger,
}) => {
  const startX = useRef(0);
  const startY = useRef(0);
  const [offsetX, setOffsetX] = useState(0);
  const [direction, setDirection] = useState<'horizontal' | 'vertical' | null>(null);
  const [exiting, setExiting] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);

  // Programmatic dismiss triggered by parent (e.g. X button)
  const dismissTriggerRef = useRef(dismissTrigger);
  useEffect(() => {
    if (dismissTrigger != null && dismissTrigger !== dismissTriggerRef.current) {
      dismissTriggerRef.current = dismissTrigger;
      if (exiting || disabled) return;
      // Measure height if not yet measured
      if (contentRef.current && measuredHeight === null) {
        setMeasuredHeight(contentRef.current.offsetHeight);
      }
      setExiting(true);
      setTimeout(() => {
        setCollapsed(true);
        setTimeout(() => {
          onDismiss();
        }, 200);
      }, 200);
    }
  }, [dismissTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (disabled || exiting) return;
      startX.current = e.touches[0].clientX;
      startY.current = e.touches[0].clientY;
      setDirection(null);
      // Measure height for collapse animation
      if (contentRef.current && measuredHeight === null) {
        setMeasuredHeight(contentRef.current.offsetHeight);
      }
    },
    [disabled, exiting, measuredHeight]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (disabled || exiting) return;
      const dx = e.touches[0].clientX - startX.current;
      const dy = e.touches[0].clientY - startY.current;

      if (!direction) {
        if (Math.abs(dx) > DIRECTION_LOCK_THRESHOLD) {
          setDirection('horizontal');
        } else if (Math.abs(dy) > DIRECTION_LOCK_THRESHOLD) {
          setDirection('vertical');
          return;
        } else {
          return;
        }
      }

      if (direction === 'vertical') return;

      // Allow swiping both left and right
      setOffsetX(dx);
    },
    [disabled, exiting, direction]
  );

  const handleTouchEnd = useCallback(() => {
    if (disabled || exiting || direction !== 'horizontal') {
      setOffsetX(0);
      setDirection(null);
      return;
    }

    if (Math.abs(offsetX) >= DISMISS_THRESHOLD) {
      setExiting(true);
      // After slide-out, collapse height, then call onDismiss
      setTimeout(() => {
        setCollapsed(true);
        setTimeout(() => {
          onDismiss();
        }, 200);
      }, 200);
    } else {
      setOffsetX(0);
    }
    setDirection(null);
  }, [disabled, exiting, direction, offsetX, onDismiss]);

  const opacity = exiting ? 0 : 1 - Math.abs(offsetX) / 300;
  const exitDirection = offsetX >= 0 ? '100%' : '-100%';
  const translateX = exiting ? exitDirection : `${offsetX}px`;
  const swipingRight = offsetX > 0;

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        height: collapsed ? 0 : measuredHeight ?? 'auto',
        transition: collapsed ? 'height 200ms ease-out' : 'none',
      }}
    >
      {/* Red background with trash icon */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          bgcolor: '#d32f2f',
          display: 'flex',
          alignItems: 'center',
          justifyContent: swipingRight || (exiting && exitDirection === '100%') ? 'flex-start' : 'flex-end',
          px: 3,
          opacity: Math.abs(offsetX) > 0 || exiting ? 1 : 0,
        }}
      >
        <DeleteIcon sx={{ color: 'white' }} />
      </Box>
      {/* Sliding content */}
      <Box
        ref={contentRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={{
          transform: `translateX(${translateX})`,
          transition: direction === 'horizontal' ? 'none' : 'transform 200ms ease, opacity 200ms ease',
          opacity,
          bgcolor: 'background.paper',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default SwipeableNotificationItem;
